// Types
import { ICart } from '@src/types/cart';
import api from 'frontend/js/api';

// Request body for fetchShoppingBasket
export interface ShoppingBasketRequestBody {
  cart: {
    token: string;
    validate_only: 'true';
    customer: RequestCustomer;
    line_items: RequestLineItems;
    discounts_applied: RequestDiscountsApplied;
    giftcards_applied: RequestGiftcardsApplied;
    discount_or_gc?: RequestDiscountOrGiftCard;
  };
}

interface RequestCustomer {
  email: string;
  custcode: string;
}

interface RequestDiscountsApplied {
  discounts_applied: any[];
}

interface RequestGiftcardsApplied {
  giftcards_applied: any[];
}

interface RequestDiscountOrGiftCard {
  discount: { card_number: string; card_pin: string; card_type: 'GIFTCARD' | 'DISCOUNT' };
}

interface RequestLineItems {
  line_items: RequestLineItem[];
}

interface RequestLineItem {
  name: string;
  sku: string;
  price: number;
  compare_price: number | null;
  quantity: number;
  discount_applied_number: string;
  unique_id: string;
}

// Argument for fetchShoppingBasket
export interface FetchShoppingBasketArgs {
  cartToken: string;
  cartData: ICart;
  discountData?: ShoppingBasketDiscountData;
  giftCartData?: ShoppingBasketGiftCardData;
}

export interface ShoppingBasketDiscountData {
  removeIndex: number;
}

export interface ShoppingBasketGiftCardData {
  removeIndex: number;
  code: string;
  pin: string;
}

// Response for fetchShoppingBasket
export interface ShoppingBasketResponse {
  success: boolean;
  cart: ResponseCart;
}

interface ResponseCart {
  cartCId: number;
  cart: ResponseCartCart;
}

interface ResponseCartCart {
  unique_id: string;
  customer: ResponseCustomer;
  new_discount_or_gc: NewDiscountOrGc;
  line_items: ResponseLineItems;
  discounts_applied: ResponseDiscountsApplied;
  giftcards_applied: ResponseGiftcardsApplied;
}

interface ResponseCustomer {
  email: string;
  custcode: string;
  store_number: null;
}

interface ResponseDiscountsApplied {
  discounts_applied: any[];
}

interface ResponseGiftcardsApplied {
  giftcards_applied: any[];
}

interface ResponseLineItems {
  line_items: ResponseLineItem[];
}

interface ResponseLineItem {
  id: string;
  unique_id: string;
  sku: string;
  rrp_price: string;
  compare_price: string;
  web_price: string;
  new_price: string;
  quantity: string;
  discount_applied_number: string;
  message: string;
  members_pricing_flag: boolean;
  discounts_applied: LineItemDiscountsApplied;
}

interface LineItemDiscountsApplied {
  discount_codes: any[];
}

interface NewDiscountOrGc {
  number: string;
  pin: string;
  type: string;
  status_code: string;
  status_message: string;
}

export default {
  async fetchShoppingBasket(arg: FetchShoppingBasketArgs): Promise<ShoppingBasketResponse> {
    const { cartToken, cartData, discountData, giftCartData } = arg;
    const { items, applied_discounts, applied_giftcards } = cartData;

    const requestBody: ShoppingBasketRequestBody = {
      cart: {
        token: cartToken,
        validate_only: 'true',
        customer: {
          email: (window as any).customer?.email || '',
          custcode: (window as any).customer?.customer_code || ''
        },
        line_items: {
          line_items: []
        },
        discounts_applied: {
          discounts_applied: []
        },
        giftcards_applied: {
          giftcards_applied: []
        }
      }
    };

    // Handle line items
    items.forEach((item) => {
      if (item.is_gwp_product || item.sku == 'GCE') return; // GCE is gift card SKU (standard Shopify product, not Shopify gift card)

      requestBody.cart.line_items.line_items.push({
        name: item.product_title,
        sku: item.sku,
        price: item.final_price * 0.01,
        compare_price: item.compare_at_price ? item.compare_at_price * 0.01 : null,
        quantity: item.quantity,
        discount_applied_number: item.properties?._discount_applied_number,
        unique_id: item.sku // Can't be line_item.key because Cart API field does not accept this format, but pretty useless field anyway
      });
    });

    // Handle existing discounts
    if (applied_discounts?.length) {
      const { removeIndex } = discountData || {};
      let discounts = [...applied_discounts];

      if (typeof removeIndex == 'number') {
        discounts.splice(removeIndex, 1);
      }

      requestBody.cart.discounts_applied['discounts_applied'] = discounts;
    }

    // Handle existing gift cards
    if (applied_giftcards?.length) {
      const { removeIndex } = giftCartData || {};
      let giftcards = [...applied_giftcards];

      if (typeof removeIndex == 'number') {
        giftcards.splice(removeIndex, 1);
      }

      requestBody.cart.giftcards_applied['giftcards_applied'] = giftcards;
    }

    // Handle new gift card
    if (giftCartData) {
      const { code, pin } = giftCartData;

      if (code && pin) {
        requestBody.cart.discount_or_gc = {
          discount: {
            card_number: code,
            card_pin: pin,
            card_type: 'GIFTCARD'
          }
        };
      } else if (code) {
        requestBody.cart.discount_or_gc = {
          discount: {
            card_number: code,
            card_pin: '',
            card_type: 'DISCOUNT'
          }
        };
      }
    }

    try {
      const res = await fetch(`${api.getBasketApiUrl()}basket/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        throw new Error('Failed to fetch shopping basket');
      }

      const jsonData = await res.json();

      return jsonData;
    } catch (error) {
      console.error('Error fetching shopping basket:', error);

      throw error;
    }
  }
};
